import { Center, Spinner, Text, VStack } from "@chakra-ui/react"
import React, { useCallback, useEffect, useState } from "react"
import { ZoneTypes, useZonesByFilterQuery, ZonesByFilterQueryVariables } from "../../graphql"
import { ZonesList } from "./ZonesList"

export type ZonesByFilterProps = {
	keyword?: string
	type?: ZoneTypes
	isActive?: boolean
}

export const ZonesByFilter: React.FC<ZonesByFilterProps> = ({ keyword, type, isActive }) => {
	const [filter, setFilter] = useState({ keyword, type, isActive })

	const [pagination, setPagination] = useState<ZonesByFilterQueryVariables["pagination"]>({ limit: 20, page: 1 })

	const [{ data, fetching, error }] = useZonesByFilterQuery({
		variables: { filter, pagination },
	})

	useEffect(() => {
		const timeoutId = setTimeout(() => {
			setFilter({ keyword, type, isActive })
		}, 400)

		return () => {
			clearTimeout(timeoutId)
		}
	}, [keyword, type, isActive])

	const nextPage = useCallback(() => {
		if (fetching) return

		if (data?.zonesByFilter.hasNextPage) {
			setPagination((prev) => ({
				...prev,
				page: (prev.page || 0) + 1,
			}))
		}
	}, [data, fetching])

	return (
		<VStack w="full" maxW="2xl" align="stretch">
			{fetching && !data?.zonesByFilter ? (
				<Center w="full" py="4">
					<VStack w="full" color="grayscale.label">
						<Text fontSize="sm">Loading zones</Text>
						<Spinner size="sm" />
					</VStack>
				</Center>
			) : error ? (
				<Center py="4">
					<Text fontSize="sm" fontWeight="semibold" color="error.500">
						{error.message.replace("[GraphQL] ", "")}
					</Text>
				</Center>
			) : data?.zonesByFilter.zones && data.zonesByFilter.zones.length ? (
				<VStack w="full" align="stretch">
					<Text>
						Showing {data.zonesByFilter.zones.length || 0} zone
						{(data.zonesByFilter.zones.length || 0) > 1 ? "s" : ""}
					</Text>

					<ZonesList zones={data.zonesByFilter.zones} loadMore={nextPage} />

					{fetching && (
						<Center w="full" py="4">
							<VStack w="full" color="grayscale.label">
								<Text fontSize="sm">Loading more zones</Text>
								<Spinner size="sm" />
							</VStack>
						</Center>
					)}
				</VStack>
			) : (
				<Center py="4">
					<Text fontSize="sm" fontWeight="semibold" color="error.500">
						Couldn&apos;t find any zones.
					</Text>
				</Center>
			)}
		</VStack>
	)
}
